import { createSelector } from '@reduxjs/toolkit';

import { AlertTypes, DeliveryType, fetchSmsAlerts, toggleSmsAlerts } from '@/redux/api/alerts';
import { UNSUBSCRIBE_SUCCESS } from '@/redux/modules/account/notifications/emailList/emailList.actions';
import { getAuthToken, isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { AppDispatch, AppGetState, GlobalState } from '@/redux/store';
import { isClientSide } from '@/utils/amplitude/analytics/core';
import { AnalyticsCTAEvent, trackCTAClickedAnalytics } from '@/utils/amplitude/analytics/cta/trackCTAClicked.analytics';
import { getFullUrl } from '@/utils/amplitude/analytics/utils/getFullUrl';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';

import {
    LOAD_SMS_ALERTS_FAIL,
    LOAD_SMS_ALERTS_REQUEST,
    LOAD_SMS_ALERTS_SUCCESS,
    TOGGLE_SMS_ALERTS_SUCCESS,
} from './actions';
import { getDeployment } from './config';

// reducer
export type SmsAlertsSlice = {
    subscribed: any[];
    subscriptions: any[];
};

// Batching state
let batchedAlerts = new Map<
    string,
    { alertTypeId: AlertTypes; deliveryType: DeliveryType; skipTracking: boolean; subscribed: boolean }
>();
let batchTimeout: ReturnType<typeof setTimeout> | null = null;
const BATCH_DELAY = 100;

// Helper to create unique key for alert
const createAlertKey = (alertTypeId: AlertTypes, deliveryType: DeliveryType) => `${alertTypeId}-${deliveryType}`;

export const defaultSmsAlertsSlice: SmsAlertsSlice = {
    subscribed: [],
    subscriptions: [],
};

export default function reducer(state: SmsAlertsSlice = defaultSmsAlertsSlice, action: any = {}): SmsAlertsSlice {
    switch (action.type) {
        case LOAD_SMS_ALERTS_SUCCESS:
            return {
                ...state,
                subscriptions: action.payload ?? [],
            };
        case TOGGLE_SMS_ALERTS_SUCCESS:
            return {
                ...state,
                subscribed: action.payload.subscribed,
            };
        case UNSUBSCRIBE_SUCCESS:
            if (action.meta.code !== 'bids_ending_soon') {
                return state;
            }
            const subscriptions = state.subscriptions.map((sub) => {
                if (sub.name === 'Bids Ending Soon') {
                    sub.subscribed = false;
                }
                return sub;
            });
            return {
                ...state,
                subscriptions,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state;
const smsAlertsSlice = createSelector(stateSelector, (state) => state.smsAlerts);

// @ts-ignore - ignoring "state"
const idSelector = (_: GlobalState, id: number) => id;

export const getSubscriptions = createSelector(smsAlertsSlice, (state) => state.subscriptions || []);
export const getSubscriptionIdList = createSelector(getSubscriptions, (subscriptions): number[] => {
    const subs = subscriptions.map((subscription) => subscription.alertTypeId) || [];

    // @ts-ignore
    return [...new Set(subs)];
});
export const getSubscriptionsById = createSelector([getSubscriptions, idSelector], (subscriptions, id) => {
    return subscriptions.filter((subscription) => subscription.alertTypeId === id);
});

export const getSubscribedToSMSAlerts = createSelector(smsAlertsSlice, (state) =>
    Boolean(state.subscriptions.find((alert) => Boolean(alert.subscribed) && alert.deliveryType === DeliveryType.sms))
);

export const getAllSMSAlertsSubscriptions = createSelector(smsAlertsSlice, (state) =>
    state.subscriptions
        .filter((alert) => Boolean(alert.subscribed) && alert.deliveryType === DeliveryType.sms)
        .reduce(
            (acc, alert) => {
                acc[alert.alertTypeId] = alert;
                return acc;
            },
            {} as Record<number, any>
        )
);

/**
 * Checks if a user is subscribed to all main SMS alert types
 * (AuctionReminder, Outbid, PaymentReminder)
 */
export const getIsSubscribedToAllMainSMSAlerts = createSelector(getAllSMSAlertsSubscriptions, (smsAlerts) => {
    const mainAlertTypes = [AlertTypes.AuctionReminder, AlertTypes.Outbid, AlertTypes.PaymentReminder];

    return mainAlertTypes.every((alertType) => smsAlerts[alertType]?.subscribed === true);
});

/**
 * Returns an array of main SMS alert types that the user is NOT subscribed to
 * (AuctionReminder, Outbid, PaymentReminder)
 */
export const getMissingMainSMSAlerts = createSelector(getAllSMSAlertsSubscriptions, (smsAlerts) => {
    const mainAlertTypes = [AlertTypes.AuctionReminder, AlertTypes.Outbid, AlertTypes.PaymentReminder];

    return mainAlertTypes.filter((alertType) => !smsAlerts[alertType]?.subscribed);
});

export const getSubscribedToBrowserAlerts = createSelector(smsAlertsSlice, (state) =>
    Boolean(
        state.subscriptions.find((alert) => Boolean(alert.subscribed) && alert.deliveryType === DeliveryType.browser)
    )
);

export const getEmailSubscriptions = createSelector(smsAlertsSlice, (state) =>
    state.subscriptions.filter((alert) => alert.deliveryType === DeliveryType.email)
);

// check for only an outbid and auction reminder
export const getSubscribedToSMSOutbidAndReminders = createSelector(smsAlertsSlice, (state) =>
    Boolean(
        state.subscriptions.find(
            (alert) =>
                Boolean(alert.subscribed) &&
                alert.deliveryType === DeliveryType.sms &&
                alert.alertTypeId === AlertTypes.Outbid
        ) &&
        state.subscriptions.find(
            (alert) =>
                Boolean(alert.subscribed) &&
                alert.deliveryType === DeliveryType.sms &&
                alert.alertTypeId === AlertTypes.AuctionReminder
        )
    )
);

// check for only an outbid and auction reminder
export const getSubscribedToBrowserOutbidAndReminders = createSelector(smsAlertsSlice, (state) =>
    Boolean(
        state.subscriptions.find(
            (alert) =>
                Boolean(alert.subscribed) &&
                alert.deliveryType === DeliveryType.browser &&
                alert.alertTypeId === AlertTypes.Outbid
        ) &&
        state.subscriptions.find(
            (alert) =>
                Boolean(alert.subscribed) &&
                alert.deliveryType === DeliveryType.browser &&
                alert.alertTypeId === AlertTypes.AuctionReminder
        )
    )
);

const shouldFetchSmsAlerts = createSelector(smsAlertsSlice, () => true);

/* ACTION CREATORS */
const loadSmsAlerts = (token?: string) => async (dispatch, getState) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);
        const authToken = getAuthToken(state);
        const loggedIn = isUserLoggedIn(state);

        if (!loggedIn) {
            return Promise.resolve();
        }

        dispatch({
            type: LOAD_SMS_ALERTS_REQUEST,
        });
        const response = await fetchSmsAlerts({
            authToken: token || authToken,
            deployment,
        });
        return dispatch({
            meta: { actionTime: Date.now() },
            payload: response.payload,
            type: LOAD_SMS_ALERTS_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            payload: error,
            type: LOAD_SMS_ALERTS_FAIL,
        });
    }
};

const executeBatchedAlerts =
    ({ onError }: { onError?: () => void }) =>
    async (dispatch: AppDispatch, getState: AppGetState) => {
        if (batchedAlerts.size === 0) {
            return;
        }

        const alertsToProcess = Array.from(batchedAlerts.values());
        const hasAnyTracking = alertsToProcess.some((alert) => !alert.skipTracking);

        batchedAlerts.clear();
        batchTimeout = null;

        try {
            const state = getState();
            const deployment = getDeployment(state);
            const authToken = getAuthToken(state);

            dispatch({
                type: LOAD_SMS_ALERTS_REQUEST,
            });

            const response = await toggleSmsAlerts({
                alertPreferences: alertsToProcess.map(({ alertTypeId, deliveryType, subscribed }) => ({
                    alertTypeId,
                    deliveryType,
                    subscribed,
                })),
                authToken,
                deployment,
            });

            if (hasAnyTracking && isClientSide()) {
                const enabledSmsAlerts = alertsToProcess.filter(
                    (alert) => alert.subscribed && alert.deliveryType === DeliveryType.sms
                );

                if (enabledSmsAlerts.length > 0) {
                    trackCTAClickedAnalytics({
                        ...AnalyticsCTAEvent.Notifications.EnableSMSNotifications,
                        pageName: getAmplitudePageNameFromPath(window.location.pathname),
                        url: getFullUrl(deployment, window.location.pathname),
                    });
                }

                const disabledSmsAlerts = alertsToProcess.filter(
                    (alert) => !alert.subscribed && alert.deliveryType === DeliveryType.sms
                );

                if (disabledSmsAlerts.length > 0) {
                    trackCTAClickedAnalytics({
                        ...AnalyticsCTAEvent.Notifications.DisableSMSNotifications,
                        pageName: getAmplitudePageNameFromPath(window.location.pathname),
                        url: getFullUrl(deployment, window.location.pathname),
                    });
                }
            }

            return dispatch({
                meta: { actionTime: Date.now() },
                payload: response.payload,
                type: LOAD_SMS_ALERTS_SUCCESS,
            });
        } catch (error) {
            onError?.();
            return dispatch({
                error: true,
                payload: error,
                type: LOAD_SMS_ALERTS_FAIL,
            });
        }
    };

export const toggleSmsAlert =
    (
        alertTypeId: AlertTypes,
        deliveryType: DeliveryType,
        subscribed: boolean,
        skipTracking: boolean = false,
        onError?: () => void
    ) =>
    async (dispatch: AppDispatch) => {
        const alertKey = createAlertKey(alertTypeId, deliveryType);
        batchedAlerts.set(alertKey, { alertTypeId, deliveryType, skipTracking, subscribed });

        if (batchTimeout) {
            clearTimeout(batchTimeout);
        }

        // Use batch timeout to group alerts into a single API call
        batchTimeout = setTimeout(() => {
            dispatch(executeBatchedAlerts({ onError }));
        }, BATCH_DELAY);
    };

export const fetchSmsAlertsIfNeeded =
    (authToken?: string, force?: boolean) => async (dispatch: AppDispatch, getState: AppGetState) => {
        if (shouldFetchSmsAlerts(getState()) || force) {
            return dispatch(loadSmsAlerts(authToken));
        }
        return Promise.resolve();
    };
